import React from "react";
import parse, {
  HTMLReactParserOptions,
  Element,
  domToReact,
  DOMNode,
} from "html-react-parser";
import { cn } from "@/lib/utils";
import { twMerge } from "tailwind-merge";

interface RichTextBlockProps {
  html: string;
  className?: string;
  paragraphClassName?: string;
  headingClassName?: string;
  listClassName?: string;
  linkClassName?: string;
}

export const RichTextBlock: React.FC<RichTextBlockProps> = ({
  html,
  className,
  paragraphClassName,
  headingClassName,
  listClassName,
  linkClassName,
}) => {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (domNode instanceof Element) {
        const { name, attribs, children } = domNode;

        // Apply styles to paragraphs
        if (name === "p") {
          return (
            <p className={cn("mb-4 text-base leading-7", paragraphClassName)}>
              {domToReact(children as DOMNode[], options)}
            </p>
          );
        }

        // Apply styles to headings — demote h1 to h2 so user-generated
        // content doesn't compete with the page's actual h1
        if (["h1", "h2", "h3", "h4", "h5", "h6"].includes(name)) {
          const rendered = domToReact(children as DOMNode[], options);
          const isEmpty =
            !rendered ||
            (typeof rendered === 'string' && !rendered.trim()) ||
            (Array.isArray(rendered) && rendered.length === 0);
          if (isEmpty) return <></>;

          const tag = name === 'h1' ? 'h2' : name;
          const headingStyles = {
            h2: "text-3xl font-medium mb-5",
            h3: "text-2xl font-medium mb-4",
            h4: "text-xl font-medium mb-3",
            h5: "text-lg font-medium mb-2",
            h6: "text-base font-medium mb-2",
          }[tag];

          return React.createElement(
            tag,
            { className: cn(headingStyles, headingClassName) },
            rendered,
          );
        }

        // Apply styles to lists
        if (name === "ul") {
          return (
            <ul className={cn("list-disc pl-5 mb-4 space-y-2", listClassName)}>
              {domToReact(children as DOMNode[], options)}
            </ul>
          );
        }

        if (name === "ol") {
          return (
            <ol
              className={cn("list-decimal pl-5 mb-4 space-y-2", listClassName)}
            >
              {domToReact(children as DOMNode[], options)}
            </ol>
          );
        }

        // Apply styles to links
        if (name === "a") {
          return (
            <a
              href={attribs.href}
              target={attribs.target || "_blank"}
              rel={attribs.rel || "noopener noreferrer"}
              className={cn("text-blue-600 hover:underline", linkClassName)}
            >
              {domToReact(children as DOMNode[], options)}
            </a>
          );
        }

        // Render images full-width with rounded corners
        if (name === "img") {
          return (
            <img
              src={attribs.src}
              alt={attribs.alt ?? ""}
              className="w-full rounded"
            />
          );
        }

        // Apply styles to blockquotes
        if (name === "blockquote") {
          return (
            <blockquote className="pl-4 border-l-4 border-gray-300 italic my-4">
              {domToReact(children as DOMNode[], options)}
            </blockquote>
          );
        }

        // Apply styles to code blocks
        if (name === "pre") {
          return (
            <pre className="bg-gray-100 p-4 rounded-md my-4 overflow-x-auto">
              {domToReact(children as DOMNode[], options)}
            </pre>
          );
        }

        // Apply styles to inline code
        if (
          name === "code" &&
          domNode.parent instanceof Element &&
          domNode.parent.name !== "pre"
        ) {
          return (
            <code className="bg-gray-100 px-1 py-0.5 rounded text-sm">
              {domToReact(children as DOMNode[], options)}
            </code>
          );
        }

        // Apply styles to tables
        if (name === "table") {
          return (
            <div className="overflow-x-auto my-4">
              <table className="min-w-full border-collapse">
                {domToReact(children as DOMNode[], options)}
              </table>
            </div>
          );
        }

        if (name === "th") {
          return (
            <th className="border border-gray-300 px-4 py-2 bg-gray-50 font-medium text-left">
              {domToReact(children as DOMNode[], options)}
            </th>
          );
        }

        if (name === "td") {
          return (
            <td className="border border-gray-300 px-4 py-2">
              {domToReact(children as DOMNode[], options)}
            </td>
          );
        }
      }
    },
  };

  return (
    <div className={twMerge("rich-text-content", className)}>
      {parse(html, options)}
    </div>
  );
};

export default RichTextBlock;
